import { anatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';

/**
 * Toggle — a two-option segmented control ("Yes"/"No", or check/close), with a
 * third state for "nothing chosen yet". Not to be confused with `ButtonToggle`
 * (an icon-only pressed button) or `Switch` (a single on/off control).
 */
const toggleAnatomy = anatomy('toggle').parts('root', 'item');

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(toggleAnatomy.keys);

export const styleConfig = defineMultiStyleConfig({
  baseStyle: definePartsStyle({
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      // Hug the sides. Without this the track shrinks in a narrow parent and
      // the labels are cut off with no ellipsis; overflowing is the louder,
      // easier-to-notice failure.
      flexShrink: 0,
      bg: 'neutral.fill-subtle',
      p: 0.5,
    },
    item: {
      // `chakra.button` carries no button reset, unlike Chakra's `Button` —
      // without these the UA's own background, border and font show through,
      // and `textStyle` looks as though it does not apply.
      appearance: 'none',
      border: 'none',
      cursor: 'pointer',
      font: 'inherit',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Sized by `minW` alone, with no horizontal padding, so "Yes" and "No"
      // measure the same and the selection cannot shift sideways.
      px: 0,
      h: 'full',
      gap: 1.5,
      whiteSpace: 'nowrap',
      transition: 'background-color 0.15s ease, color 0.15s ease',
      bg: 'transparent',
      color: 'neutral.fg-primary',
      _hover: {
        bg: 'neutral.fill-subtle',
      },
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'neutral.fg-primary',
        outlineOffset: '2px',
      },
      // `_checked` is `&[aria-checked=true], &[data-checked]`, which the
      // `role="radio"` sides already satisfy — no extra data attribute needed.
      // The nested `_hover` restatements keep the pseudo precedence explicit
      // rather than leaving it to emitted key order.
      _checked: {
        bg: 'brand.solid-primary',
        color: 'misc.white-fix-primary',
        _hover: {
          bg: 'brand.solid-hover',
        },
      },
      _disabled: {
        cursor: 'not-allowed',
        bg: 'transparent',
        color: 'neutral.fg-tertiary',
        _hover: {
          bg: 'transparent',
        },
        _checked: {
          bg: 'neutral.solid-muted',
          color: 'neutral-invert.fg-primary',
          _hover: {
            bg: 'neutral.solid-muted',
          },
        },
      },
    },
  }),

  sizes: {
    sm: definePartsStyle({
      root: {
        h: 7, // 28px
        borderRadius: 'md', // 6px — sm draws its own radii, one step down
      },
      item: {
        // The sizes scale has no 38px step, so this is a literal, in px as the
        // scale's own custom fixed sizes (`container`) are.
        minW: '38px',
        borderRadius: 'base', // 4px
        textStyle: 'body-300',
        fontWeight: 'semibold',
      },
    }),
    md: definePartsStyle({
      root: {
        h: 9, // 36px
        borderRadius: 'lg', // 8px
      },
      item: {
        // As above: no 52px step on the scale.
        minW: '52px',
        borderRadius: 'md', // 6px
        textStyle: 'body-500',
        fontWeight: 'medium',
      },
    }),
    lg: definePartsStyle({
      root: {
        h: 12, // 48px
        borderRadius: 'lg',
      },
      item: {
        minW: '52px',
        borderRadius: 'md',
        textStyle: 'body-500',
        fontWeight: 'medium',
      },
    }),
  },

  variants: {
    grow: definePartsStyle({
      root: {
        w: 'full',
        flexShrink: 1,
      },
      item: {
        flex: 1,
      },
    }),
  },

  defaultProps: {
    size: 'md',
  },
});
